import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Lock, XCircle } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { Section } from "../components/site/blocks";
import { Logo } from "../components/site/Logo";
import { PaymentConsents, validateConsents } from "../components/site/PaymentConsents";
import { api, formatApiError } from "../lib/api";
import { COMPANY, euros } from "../data/site";

export default function PaymentLink() {
  useSeo("Paiement sécurisé", "Règlement sécurisé par carte d'un acompte sur devis Habitalys.");
  const { token } = useParams();
  const [link, setLink] = useState(undefined);
  const [profile, setProfile] = useState("particulier");
  const [cgv, setCgv] = useState(false);
  const [ack, setAck] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  useEffect(() => {
    api.get(`/payment-links/${token}`).then((r) => setLink(r.data))
      .catch(() => setLink({ state: "invalid", message: "Ce lien de paiement n'est plus valide. Contactez-nous." }));
  }, [token]);

  const pay = async () => {
    const e = validateConsents(profile, cgv, ack);
    setErrors(e); setServerError("");
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const r = await api.post(`/payment-links/${token}/checkout`, { profile, cgv, withdrawal_ack: ack, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      const st = err.response?.status;
      if (st === 409) setLink((l) => ({ ...l, state: "paid", message: err.response.data.detail }));
      else if (st === 410 || st === 404) setLink((l) => ({ ...l, state: "invalid", message: err.response.data.detail }));
      else setServerError(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Section className="bg-mist" testid="payment-link-page">
      <div className="max-w-2xl mx-auto rounded-[28px] bg-white border border-navy/10 p-6 sm:p-10 shadow-[0_30px_60px_-40px_rgba(16,36,62,0.45)]">
        <Logo testid="payment-link-logo" />
        {link === undefined && <p className="mt-8 text-slate-600 flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Chargement…</p>}
        {link?.state === "paid" && (
          <div className="mt-8" data-testid="payment-link-paid">
            <CheckCircle2 className="h-12 w-12 text-green-700" aria-hidden="true" />
            <h1 className="mt-4 text-3xl">{link.message}</h1>
            {link.reference && <p className="mt-3 text-slate-700">Référence : <strong>{link.reference}</strong></p>}
          </div>
        )}
        {link?.state === "invalid" && (
          <div className="mt-8" data-testid="payment-link-invalid">
            <XCircle className="h-12 w-12 text-red-700" aria-hidden="true" />
            <h1 className="mt-4 text-3xl">{link.message}</h1>
            <p className="mt-3 text-slate-700">Écrivez-nous à <a href={`mailto:${COMPANY.email}`} className="link-u text-navy font-semibold">{COMPANY.email}</a> ou via le <Link to="/contact" className="link-u text-navy font-semibold">formulaire de contact</Link>.</p>
          </div>
        )}
        {link?.state === "valid" && (
          <div className="mt-8 space-y-6" data-testid="payment-link-valid">
            <div>
              <p className="eyebrow">Acompte sur devis</p>
              <h1 className="mt-3 text-3xl sm:text-4xl">Bonjour {link.client_name}</h1>
            </div>
            <dl className="rounded-2xl bg-mist-50 border border-navy/10 divide-y divide-navy/10">
              <div className="p-4 grid sm:grid-cols-[160px,1fr] gap-1"><dt className="text-slate-600">Référence</dt><dd className="font-semibold text-navy" data-testid="payment-link-reference">{link.reference}</dd></div>
              <div className="p-4 grid sm:grid-cols-[160px,1fr] gap-1"><dt className="text-slate-600">Objet</dt><dd className="text-slate-900" data-testid="payment-link-description">{link.description}</dd></div>
              <div className="p-4 grid sm:grid-cols-[160px,1fr] gap-1"><dt className="text-slate-600">Montant TTC</dt><dd className="font-display text-3xl text-navy" data-testid="payment-link-amount">{euros(link.amount_cents)}</dd></div>
            </dl>
            <PaymentConsents profile={profile} setProfile={setProfile} cgv={cgv} setCgv={setCgv} ack={ack} setAck={setAck} errors={errors} />
            {serverError && <p role="alert" className="rounded-xl bg-red-50 text-red-800 p-4 font-medium">{serverError}</p>}
            <button type="button" onClick={pay} className="btn-accent w-full text-lg py-4" disabled={loading} data-testid="payment-link-pay-button">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Lock className="h-5 w-5" aria-hidden="true" />} Payer par carte
            </button>
            <p className="text-sm text-slate-600">Paiement sécurisé par Stripe. Habitalys n'a jamais accès à vos coordonnées bancaires.</p>
          </div>
        )}
      </div>
    </Section>
  );
}
